import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { productService, categoryService } from '../services/api';
import ProductCard from '../components/products/ProductCard';

// Importar imágenes de portada
import portada01 from '../assets/portada/01.png';
import portada02 from '../assets/portada/02.png';
import portada03 from '../assets/portada/03.png';
import portada04 from '../assets/portada/04.png';
import portada05 from '../assets/portada/05.png';
import portada06 from '../assets/portada/06.png';
import portada07 from '../assets/portada/07.png';
import portada09 from '../assets/portada/09.png';

const heroImages = [portada01, portada02, portada03, portada04, portada05, portada06, portada07, portada09];

// Cache global para no recargar al volver
const cache = { categories: null, products: {} };

const Home = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(cache.categories || []);
  const [loading, setLoading] = useState(!cache.categories);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);

  // Touch/drag state para el carrusel
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const hasSearchParams = searchParams.get('search') || searchParams.get('category');

  useEffect(() => {
    if (hasSearchParams) {
      navigate(`/productos?${searchParams.toString()}`, { replace: true });
    } else {
      loadCategories();
    }
  }, [hasSearchParams, searchParams, navigate]);

  // Auto-play del carrusel
  useEffect(() => {
    if (!isAutoPlaying) return;
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => prev >= 100 ? 100 : prev + 2);
    }, 100);

    const slideInterval = setInterval(() => {
      setPreviousSlide(currentSlide);
      setIsTransitioning(true);
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
      setTimeout(() => setIsTransitioning(false), 700);
    }, 5000);

    return () => {
      clearInterval(slideInterval);
      clearInterval(progressInterval);
    };
  }, [isAutoPlaying, currentSlide]);

  // Solo carga las categorías si no están en cache
  const loadCategories = async () => {
    if (cache.categories) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await categoryService.getAll();
      const categoriesData = response.data.data || response.data || [];
      cache.categories = categoriesData;
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchEnd(e.touches[0].clientX);
    setIsDragging(true);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentTouch = e.touches[0].clientX;
    setTouchEnd(currentTouch);
    setDragOffset(currentTouch - touchStart);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      diff > 0 ? nextSlide() : prevSlide();
    }
    setDragOffset(0);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  if (hasSearchParams) return null;

  const getKenBurnsAnimation = (index) => {
    const animations = [
      'animate-ken-burns-zoom-in',
      'animate-ken-burns-zoom-out',
      'animate-ken-burns-pan-left',
      'animate-ken-burns-pan-right',
      'animate-ken-burns-pan-up',
    ];
    return animations[index % animations.length];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Carrusel */}
      <section className="relative w-full overflow-hidden bg-[#2d3c5d]">
        <div
          className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-out ${
                currentSlide === index
                  ? 'opacity-100 z-10 scale-100'
                  : previousSlide === index && isTransitioning
                    ? 'opacity-0 z-5 scale-105'
                    : 'opacity-0 z-0 scale-95'
              }`}
              style={{
                transform: isDragging && currentSlide === index ? `translateX(${dragOffset}px)` : undefined,
              }}
            >
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className={`w-full h-full object-cover transition-transform duration-[5000ms] ease-out ${
                    currentSlide === index ? getKenBurnsAnimation(index) : ''
                  }`}
                  draggable={false}
                  style={{ transformOrigin: index % 2 === 0 ? 'center center' : 'top left' }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20" />
            </div>
          ))}

          {/* Controles */}
          <button
            onClick={() => {
              setPreviousSlide(currentSlide);
              setIsTransitioning(true);
              prevSlide();
              setIsAutoPlaying(false);
              setTimeout(() => { setIsTransitioning(false); setIsAutoPlaying(true); }, 10000);
            }}
            className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 z-20 border border-white/20"
          >
            <ChevronLeft className="w-7 h-7 text-white" />
          </button>

          <button
            onClick={() => {
              setPreviousSlide(currentSlide);
              setIsTransitioning(true);
              nextSlide();
              setIsAutoPlaying(false);
              setTimeout(() => { setIsTransitioning(false); setIsAutoPlaying(true); }, 10000);
            }}
            className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 z-20 border border-white/20"
          >
            <ChevronRight className="w-7 h-7 text-white" />
          </button>

          {/* Indicadores */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setPreviousSlide(currentSlide);
                  setIsTransitioning(true);
                  goToSlide(index);
                  setTimeout(() => setIsTransitioning(false), 700);
                }}
                className="group relative"
              >
                <div className={`relative transition-all duration-500 rounded-full overflow-hidden ${
                  currentSlide === index ? 'w-12 h-3 bg-white/30' : 'w-3 h-3 bg-white/40 hover:bg-white/60'
                }`}>
                  {currentSlide === index && (
                    <div
                      className="absolute inset-y-0 left-0 bg-white rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Contador */}
          <div className="absolute top-6 right-6 z-20 hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/10">
            <span className="text-white font-bold text-lg">{String(currentSlide + 1).padStart(2, '0')}</span>
            <span className="text-white/50">/</span>
            <span className="text-white/70 text-sm">{String(heroImages.length).padStart(2, '0')}</span>
          </div>

          {/* Swipe indicator móvil */}
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 md:hidden z-20 animate-pulse">
            <div className="flex items-center gap-2 text-white/60 text-xs">
              <ChevronLeft className="w-4 h-4" />
              <span>Desliza</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </section>

      {/* Productos por Categoría - Carga Lazy */}
      <div className="container mx-auto px-4 py-10 md:py-16">
        {loading ? (
          <div className="space-y-16">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-10 bg-gray-200 rounded-lg w-48"></div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="bg-white rounded-2xl overflow-hidden shadow-sm">
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-4 space-y-3">
                        <div className="h-5 bg-gray-200 rounded"></div>
                        <div className="h-6 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-16">
            {categories.map((category) => (
              <CategorySection
                key={category.id}
                category={category}
              />
            ))}

            {categories.length === 0 && (
              <div className="text-center py-20">
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay productos disponibles</h3>
                <p className="text-gray-500 mb-6">Estamos trabajando para agregar nuevos productos pronto.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente de categoría - carga TODOS los productos, usa cache global
const CategorySection = ({ category }) => {
  const [products, setProducts] = useState(() => cache.products[category.id] || []);
  const [loading, setLoading] = useState(() => !cache.products[category.id]);

  useEffect(() => {
    if (cache.products[category.id]) {
      setProducts(cache.products[category.id]);
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const response = await productService.getByCategory(category.id, { limit: 500 });
        const data = response.data.data || response.data || [];
        cache.products[category.id] = data;
        setProducts(data);
      } catch (error) {
        console.error(`Error loading products for ${category.name}:`, error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category.id]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="scroll-mt-32" id={`category-${category.id}`}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-2 h-10 bg-gradient-to-b from-[#2d3c5d] to-[#eddacb] rounded-full"></div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#2d3c5d]">
            {category.name}
          </h2>
        </div>

        <div className="hidden sm:block flex-1 h-px bg-gradient-to-r from-gray-200 via-gray-300 to-transparent"></div>

        {products.length > 0 && (
          <span className="text-sm text-gray-500">
            {products.length} productos
          </span>
        )}
      </div>

      {/* Productos */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {[1, 2, 3, 4].map((j) => (
            <div key={j} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
              <div className="aspect-square bg-gray-200"></div>
              <div className="p-4 space-y-3">
                <div className="h-5 bg-gray-200 rounded"></div>
                <div className="h-6 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </section>
  );
};

export default Home;
